import { AuthenticationCategory_AuthMethod as AuthMethod } from '@opendoor/observability/pbs/journey/data/categories/authentication_category_pb';
import { z } from 'zod';

const COOKIE_NAME = 'odauth_last_auth_method';

// The storable snake_case values Rails writes to humans.last_web_auth_method.
// Parsing the cookie against this enum (not a bare string) fixes the boundary
// type and keeps an attacker-controlled value from resolving an inherited
// Object.prototype key on the lookup below. session_already_exists /
// method_unspecified are never written.
const CookieAuthMethodSchema = z.enum(['email_password', 'lite', 'google', 'facebook', 'apple']);
export type CookieAuthMethod = z.infer<typeof CookieAuthMethodSchema>;

const METHOD_BY_COOKIE_VALUE: Record<CookieAuthMethod, AuthMethod> = {
  email_password: AuthMethod.EMAIL_PASSWORD,
  lite: AuthMethod.LITE,
  google: AuthMethod.GOOGLE,
  facebook: AuthMethod.FACEBOOK,
  apple: AuthMethod.APPLE,
};

// ActiveSupport::MessageVerifier envelope for a cookie written with an expiry:
// the base64-decoded outer payload is { "_rails": { "message": base64(json),
// "exp": iso8601, "pur": null } }. `_rails` is Rails' wire key; reached by
// bracket access so it doesn't read as our own dangling-underscore identifier.
const SignedEnvelopeSchema = z
  .object({ _rails: z.object({ message: z.string() }) })
  .transform((envelope) => envelope['_rails'].message);

/**
 * The last method a returning seller used to sign in on the web, read from the
 * first-party `odauth_last_auth_method` cookie the Rails web app sets on login
 * success, typed as the canonical AuthMethod enum. Returns undefined when the
 * cookie is absent, unreadable, or carries an unrecognized value (e.g. a
 * returning seller on a fresh device).
 *
 * The cookie is a Rails *signed* cookie; its wire value is `base64(outer)--hmac`
 * (see SignedEnvelopeSchema). This decoder mirrors MessageVerifier's format and
 * does NOT verify the HMAC — the value is a pure display hint, never an auth or
 * authorization signal, so its integrity is not relied on. Producer:
 * Humans::LoginConcerns#set_last_web_auth_method_cookie.
 */
export function getLastUsedWebAuthMethod(): AuthMethod | undefined {
  const method = decodeCookieAuthMethod(readRawCookie(COOKIE_NAME));
  return method === undefined ? undefined : METHOD_BY_COOKIE_VALUE[method];
}

/** The AccountFormV2 surface that serves a given auth method. */
export type AuthMethodCategory = 'email' | 'provider';

/**
 * The specific AccountFormV2 affordance a returning seller last used: the
 * badgeable subset of `AuthMethod`, narrowed once by `toLastUsedMethod`.
 *
 * Modules speak this rather than the enum so that a method with no button to
 * badge is unrepresentable downstream — `FACEBOOK` is storable and has no
 * AccountFormV2 button, and a comparison against the enum could name it.
 */
export type LastUsedMethod = 'email-password' | 'magic-link' | 'google' | 'apple';

// The one filter for "does this stored method have an affordance to badge".
// FACEBOOK is storable but has no AccountFormV2 button, so it must drop here
// rather than at the badge: the ordering resolver downstream would otherwise
// try to order a button that does not exist. session_already_exists and
// method_unspecified are never written (see CookieAuthMethodSchema).
const LAST_USED_BY_METHOD: Record<AuthMethod, LastUsedMethod | undefined> = {
  [AuthMethod.EMAIL_PASSWORD]: 'email-password',
  [AuthMethod.LITE]: 'magic-link',
  [AuthMethod.GOOGLE]: 'google',
  [AuthMethod.APPLE]: 'apple',
  [AuthMethod.FACEBOOK]: undefined,
  [AuthMethod.SESSION_ALREADY_EXISTS]: undefined,
  [AuthMethod.METHOD_UNSPECIFIED]: undefined,
};

// Total because LastUsedMethod is already post-filter: grouping cannot drop a
// method that LAST_USED_BY_METHOD kept. email-password and magic-link are both
// served by the email field and its "Continue with email" CTA, so neither has a
// button of its own.
const CATEGORY_BY_LAST_USED: Record<LastUsedMethod, AuthMethodCategory> = {
  'email-password': 'email',
  'magic-link': 'email',
  google: 'provider',
  apple: 'provider',
};

/**
 * The affordance a stored method maps to, or undefined when it has none in
 * AccountFormV2.
 */
export function toLastUsedMethod(method: AuthMethod): LastUsedMethod | undefined {
  return LAST_USED_BY_METHOD[method];
}

/** The surface a badgeable method should badge and order against. */
export function category(method: LastUsedMethod): AuthMethodCategory {
  return CATEGORY_BY_LAST_USED[method];
}

function readRawCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const prefix = `${name}=`;
  const entry = document.cookie.split('; ').find((candidate) => candidate.startsWith(prefix));
  if (entry === undefined) {
    return undefined;
  }
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    return undefined;
  }
}

function decodeCookieAuthMethod(raw: string | undefined): CookieAuthMethod | undefined {
  if (raw === undefined) {
    return undefined;
  }
  const envelope = SignedEnvelopeSchema.safeParse(parseJson(base64ToUtf8(raw.split('--')[0])));
  if (!envelope.success) {
    return undefined;
  }
  const method = CookieAuthMethodSchema.safeParse(parseJson(base64ToUtf8(envelope.data)));
  return method.success ? method.data : undefined;
}

function parseJson(text: string | undefined): unknown {
  if (text === undefined) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed;
  } catch {
    return undefined;
  }
}

function base64ToUtf8(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  try {
    // MessageVerifier uses standard base64; normalize URL-safe variants and
    // restore padding defensively before atob.
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return atob(padded);
  } catch {
    return undefined;
  }
}
